<template lang="pug">
    .container
        fan-loader(
        :loading="true"
        )
        div(slot="loadingMsg"
            class="full-width text-xs-center headline my-2") Verifying Easy link. Please wait...

        v-dialog(
            v-model='dialog' 
            persistent
            width='500px'
        )
            v-card
                v-card-title.wf-justify-content-center
                v-card-text
                    h3 In order to proceed, the loan amount must be greater than ${{ minLoanAmount }}. Would you like to increase your loan amount?
                v-spacer
                v-btn(
                    color='secondary' 
                    @click='proceedWithMinLoanAmount()' 
                    flat
                ) Yes
                v-btn(
                    color='secondary' 
                    @click='redirectToErrorPage()' 
                    flat
                ) No
</template>

<script>
  import axios from 'axios'
  import { mapState, mapGetters, mapMutations, mapActions } from 'vuex'
  import envVars from '@/env_vars'
  import lenders from '@/helpers/lenders'
  import cognitoClient from '@/helpers/cognitoClient'
  import FanLoader from 'Components/FanLoader.vue'
  import {getDealer,greenskyStatusCheck} from '@/helpers/c4cAPIClient'
  import { numberWithCommas } from "@/helpers/parsingFunctions"

export default {
    data() {
        return {
            cognitoClient,
            envVars,
            lenders,
            loading: true,
            dialog: false,
            minLoanAmount: null,
            applicationData: {},
            matchedPlans: []
        }
    },

    components: {
        FanLoader,
    },

    computed: {
        ...mapGetters([
            'greenskyPlans',
            'wellsfargoPlans',
            'marlinURL',
            'availableLenders',
            'moneyInt'
        ]),
        ...mapState({
            userEmail: state => state.user.email,
            userLastName: state => state.user.lastName,
            userFullName: state => state.user.dealerName,
            dealerPlans: state => state.user.dealerPlans
        }),
    },

    methods: {
        ...mapMutations([
            'openDialog',
            'setState',
            'updateLoanObject',
            'setLastName'
        ]),

        ...mapActions([
            'authenticateUser',
            'error',
            'hardSSNReset',
            'loanApplicationClear',
            'setPool',
            'trackEasyLink',
            'getUserInfo',
            'savePreQualId',
            'saveHasPreQual',
            'resetAppState',
            'getDealerPlans'
        ]),

        //Using async/await syntax
        async authenticateEasyLinkUser(data) {
            await this.resetAppState()
            // Setup for authenticating
            const {UserPoolId, ClientId} = this.envVars.dealerCustomer
            const pool = this.cognitoClient.createUserPool(
                UserPoolId, ClientId
            )
            //1. Set up pool and create cognito user
            try {
                await this.setPool(pool)
                this.cognitoClient.createCognitoUser(data.guid, pool)
            } catch (error) {
                //We assume this message indicates that the easy link opened was created in a different environment
                //ie: We opened a PROD Easy Link in STG conditions.
                if (data.err.message === "Username/client id combination not found.") {
                    this.error(`Looks like this Easy Link was produced from a different environment. <br> Contact your contractor to get an Easy Link sent from their account.`)
                    //We assume this message indicates that the verification window for the easylink has expired.
                } else if (data.err.message === 'Invalid code provided, please request a code again.') {
                    this.error('Looks like your Easy Link expired. <br> Please ask your contractor to send you a new Easy Link.')
                } else {
                    this.error(data.err.message)
                }
                return
            }
            //2. Authenticate User
            try {
                await this.authenticateUser({username: data.guid, password: data.password, pool, easyLinkAuth: true})
            } catch (error) {
                console.log('There was an error logging in', error)
                this.$router.push('/login')
            }

        },
        async checkMinLoanAmount(verifiedData) {
            if(verifiedData.source === 'comfortbutton' || verifiedData.loanPlanLender === 'fortiva' || verifiedData.loanPlanLender === 'marlin'){
                this.checkLenderAndRedirect(verifiedData)
            } else {
                if (this.greenskyPlansApiInitiated(verifiedData)) { 
                    this.matchedPlans = this.dealerPlans.filter(plan => plan.id == verifiedData.loanPlanId)
                } else {
                    // OCA passes loanPlanId as a string and C4C passes loanPlanId as a number
                    this.matchedPlans = this.dealerPlans.filter(plan => plan.planId == verifiedData.loanPlanId) 
                }
                if (this.matchedPlans < 1) {
                    await this.error(`We were unable to find or match a plan for this Easy Link.`)
                } else {
                    if (this.matchedPlans[0].minLoanAmount) {
                        if ( this.moneyInt(verifiedData.loanAmount) < this.matchedPlans[0].minLoanAmount) {
                            this.minLoanAmount = this.matchedPlans[0].minLoanAmount
                            return this.dialog = true
                        }
                    }
                    this.checkLenderAndRedirect(verifiedData)
                }
            }
        },
        async checkLenderAndRedirect(verifiedData) {
            // OCA's verified easy link data does not include a merchantNumber.
            // Try to find merchantNumber using availableLenders state getter.
            // availableLenders is set in setStoreData() method by calling the getDealer endpoint.
            if (verifiedData.source === 'oncallair') {
                const lender = this.availableLenders.find(lender => lender.lender_code === verifiedData.loanPlanLender)
                verifiedData.merchantNumber = lender.merchant_id
            }
            //1. Redirects based on if loanPlanLender is Marlin
            if (verifiedData.loanPlanLender === 'marlin') {
                window.location.href = this.marlinURL
            }
            //2. Redirects based on if loanPlanLender is Greensky
            else if (verifiedData.loanPlanLender === 'greensky') {
                try {
                    ///First, run merchant validation call
                    const validationResult = await greenskyStatusCheck(verifiedData.merchantNumber, {
                        email: this.userEmail,
                        lastName: this.userLastName
                    })
                    if (!validationResult.data.isValid) {
                        return await this.error("Sorry, dealer’s merchant Account is not active. Please contact your dealer.")
                    }
                    else if (validationResult.data.greenskyTraining.duplicateAccounts){
                        return await this.error("Sorry, we found multiple accounts associated with the dealer. Please contact your dealer.")
                    }
                    else if (validationResult.data.greenskyTraining.status === 'Required'){
                        return await this.error("Sorry, your dealer needs to complete their training before running an application")
                    }
                    else if (validationResult.data.greenskyTraining.status === 'InvalidResponse'){
                        return await this.error("Sorry, there was a problem validating dealer’s merchant account. Please your contact dealer.")
                    }
                    const parsedPlan = this.matchedPlans.map(({
                        planId,
                        id,
                        merchantFee,
                        planDescription,
                        planDescriptionLong,
                        planNumber,
                        programTypeId
                    }) =>
                    ({
                        id: planId || id, 
                        merchant_fee: merchantFee,
                        plan_description: planDescription,
                        plan_description_long: planDescriptionLong,
                        plan_number: planNumber,
                        program_type_id: programTypeId, 
                    }))
                    const plan = parsedPlan[0]

                    ///PREQUALIFICATION RESULTS WITH PREQUAL ID FLOW/////
                    ///Save the result of hasPreQual in state
                    this.saveHasPreQual(validationResult.data.isPreQualAllowed)
                    ////if there is a preQualificationId in the link's payload, save it in state and route to the results page
                    if (verifiedData.preQualificationId) {
                        this.loanApplicationClear()
                        if (this.matchedPlans.length === 1) {
                            this.setState(['loanApplication.loanPlan', plan])
                        }
                        else {
                            this.error(`${this.matchedPlans.length} matching plans found`)
                            throw new Error(`${this.matchedPlans.length} matching plans found`)
                        }
                        //Clear SSN from mock data if not in production
                        if (
                            this.envVars.environment === 'development'
                            || this.envVars.environment === 'staging'
                            || this.envVars.environment === 'demo'
                        ) {
                            this.hardSSNReset('')
                        }
                        this.savePreQualId(verifiedData.preQualificationId)
                        this.setState(['easyLink.startPage', '/prequal-results'])
                        this.$router.push('/prequal-results')
                        this.openDialog({
                            component: 'easy-link-welcome-dialog'
                        })
                        return
                    }
                    /////NON-PREQUALIFICATION ID FLOW////
                    //Saving the result of the greenskyStatusCheck allows starting on form-page-one to be for either a credit or prequalification application
                    ////A. First, get all the greensky plans and filter them for plans that match our data received from our axios call that runs
                    /// on mounted()
                    ////B. If there is a matched plan, set the loanPlan to that plan and re-route the user to application page 1
                    if (this.matchedPlans.length === 1) {
                        this.setState(['loanApplication.loanPlan', plan])
                        this.setState(['easyLink.startPage', '/credit-application/form-page-one'])
                        this.$router.push('/credit-application/form-page-one')
                        this.openDialog({
                            component: 'easy-link-welcome-dialog'
                        })
                    }
                    else {
                        this.error(`${this.matchedPlans.length} matching plans found`)
                        throw new Error(`${this.matchedPlans.length} matching plans found`)
                    }
                    ///PREQUALIFICATION RESULTS WITH NO PREQUAL ID FLOW/////
                    if (this.matchedPlans.length === 1 && !verifiedData.preQualificationId && validationResult.data.isPreQualAllowed) {
                        this.openDialog({
                            component: 'pre-qualification-dialog'
                        })
                    }
                    ////C. If there is no loanPlanId selected, re-direct them to pick a plan
                    if (!verifiedData.loanPlanId) {
                        this.setState(['easyLink.startPage', '/credit-application/plans'])
                        this.$router.push('/credit-application/plans')
                    }
                } catch (error) {
                    console.log('Problem getting a selected GreenSky plan', error)
                    this.error('Could not get the selected GreenSky plan')
                }
            }
            //// 3. Redirects based on if the loanPlanLender is wells fargo
            else if (verifiedData.loanPlanLender === 'wellsfargo') {
                if(verifiedData.c4cApplicationId){
                    this.$router.push(`/credit-application/wellsfargo-results/${verifiedData.c4cApplicationId}?A=`)
                } else {
                    this.$router.push(`/credit-application/account-inquiry`)
                }
            }
            //// 4. Redirects based on if the loanPlanLender is neither greensky nor marlin
            else {
                try {
                    // if(!verifiedData.loanPlanId){
                    //     this.setState(['easyLink.startPage', '/credit-application/plans'])
                    //     this.$router.push('/credit-application/plans')
                    //   }
                    // Redirects if the source is comfortbutton
                    if (verifiedData.source === 'comfortbutton') {
                        this.setState(['easyLink.comfortButton', true])
                        this.setState(['easyLink.startPage', '/credit-application/plans'])
                        this.$router.push('/credit-application/plans')
                    } else {
                        this.setState(['easyLink.startPage', '/credit-application/form-page-one'])
                        this.$router.push('/credit-application/form-page-one')
                    }
                    this.openDialog({
                        component: 'easy-link-welcome-dialog'
                    })
                } catch (error) {
                    console.log('Problem getting a plan for you', error)
                    this.error('Could not retrieve a plan for you')
                }
            }
        },

        async setStoreData(data) {
            let dealer
            if (this.greenskyPlansApiInitiated(data)) {
                dealer = await this.getDealerPlans({dealerId: data.dealerId})
            } else {
                dealer = await getDealer(data.dealerId)
            }
            //Here we transform the casing of each key to match that of our conduit responses. Our components
            //and store modules are all built with this 'snake casing.'
            const availableLenders = dealer.data.lenders.map(({
                                                                  lenderId,
                                                                  merchantId,
                                                                  locationId,
                                                                  dealerLenderId,
                                                                  lenderCode,
                                                                  active,
                                                                  lenderPriority
                                                              }) =>
                ({
                    lender_id: lenderId.toString(),
                    merchant_id: merchantId,
                    location_id: locationId,
                    dealer_lender_id: dealerLenderId.toString(),
                    lender_code: lenderCode,
                    active,
                    lender_priority: lenderPriority
                }))

            this.setState([
                'user.availableLenders', availableLenders
            ])

            if (data.loanPlanLender === 'wellsfargo') {
                this.setState(['user.dealerContactInfo', {
                    fistName: dealer.data.contactFirstName,
                    lastName: dealer.data.contactLastName,
                    companyName: dealer.data.companyName,
                    email: dealer.data.contactEmail,
                    phoneNumber: dealer.data.contactPhoneNumber,
                    address: {
                        addressLine1: dealer.data.address1,
                        city: dealer.data.city,
                        state: dealer.data.state,
                        postalCode: dealer.data.postalCode
                    }
                }])
                const actualLender = availableLenders.filter((lender)=>{
                        if(lender.lender_code === "wellsfargo") return lender
                    })
                const merchantID = actualLender[0].merchant_id
                this.setState(['loanApplication.loanPlan.planId', data.loanPlanId])
                this.setState([
                    'user.lenders.wellsfargo',
                    {merchant_id: merchantID}
                ])
            }

            this.setState(['user.companyName', dealer.data.companyName])
            this.setState(['user.phoneNumber', dealer.data.contactPhoneNumber])
            this.setState(['user.dealerId', data.dealerId])
            this.setState(['user.easyLink', true])
            this.setState(['user.email', data.dealerEmail])
            this.setState(['user.dealerName', data.dealerName])
            this.setState(['user.dealerAddress.addressLine1', dealer.data.address1])
            this.setState(['user.dealerAddress.city', dealer.data.city])
            this.setState(['user.dealerAddress.state', dealer.data.state])
            this.setState(['user.dealerAddress.postalcode', dealer.data.postalCode])
            this.setState(['user.lastName', this.getLastName(data.dealerName)])
            if (this.greenskyPlansApiInitiated(data)) {
                this.setState(['user.dealerPlans', this.$store.state.user.rawPlans])
            } else {
                this.setState(['user.dealerPlans', dealer.data.dealerPlans])
            }
            this.setState(['easyLink.active', true])
            this.setState(['easyLink.guid', data.guid])
            //Should we set this as a conditional?
            //if(data.loanPlanLender === 'greensky')
            this.setState([
                'user.lenders.greensky',
                {merchant_id: data.merchantNumber}
            ])
            this.setState(['loanApplication.loanAmount', data.loanAmount])

            if (typeof (data.loanPlanLender) === 'string') {
                this.setState(['loanApplication.lender', this.lenders[data.loanPlanLender]])
            } else if (data.loanPlanLender !== undefined) {
                this.setState(['loanApplication.lender', data.loanPlanLender])
            }
        },
        greenskyPlansApiInitiated(data) {
            return data.loanPlanLender === 'greensky' && data.source !== 'oncallair'
        },
        
        getLastName(fullName) {
            let lastName = ""
            let lastNamePart = false

            for (let i = 0; i < fullName.length; i++) {
                if (lastNamePart) {
                    lastName += fullName[i]
                }

                if (fullName[i] === " ") {
                    lastNamePart = true
                }
            }

            return lastName.trim()
        },
        proceedWithMinLoanAmount() {
            this.setState(['loanApplication.loanAmount', `$${numberWithCommas(this.minLoanAmount)}`])
            this.checkLenderAndRedirect(this.applicationData)
        },
        redirectToErrorPage() {
            this.error('We could not continue with your application. Please contact your Contractor to request a new application or discuss alternatives.')
        }
    },

    async mounted() {
        //1. Load user info
        this.getUserInfo()
        //2. call /easy-link/verify
        let verifiedData
        try {
            const verification = await axios.get(
                `${envVars.slsBaseUrl}/authenticate/easy-link/verify/${this.$route.params.guid}?code=${this.$route.query.code}`
            )
            verifiedData = verification.data
        } catch (error) {

            console.log('There was a problem authenticating you', error)
            this.error('There was a problem authenticating you')
        }
        //3. Pass back an error message if verifiedData has one
        if (verifiedData.err) {
            this.error(`${verifiedData.message}: ${verifiedData.err.message}`)
        }
        //4. Run our authenticateEasyLink flow
        this.applicationData = verifiedData
        await this.authenticateEasyLinkUser(verifiedData)

        //5. Track when the easy link was opened
        this.trackEasyLink({
            type: 'open',
            at: new Date(),
            guid: verifiedData.guid,
        })
        //6. Set the available lenders for this user with the response from the verification call
        await this.setStoreData(verifiedData)
        // 7. Redirect to appropriate outside URL or page on the app
        await this.checkMinLoanAmount(verifiedData)
    },
}
</script>

<style scoped>
.container {
    max-width: 500px;
}
</style>
